import { z } from 'zod';
import type {
  InteractivePtyScreen,
  RunCommandResult,
} from '../../../../types/command-executor.types';
import { useBottomPanelStore } from '../../../../store/bottomPanelStore';
import { useTerminalStore } from '../../../../store/terminalStore';
import {
  INTERACTIVE_SESSION_TOOL_NAME,
  KILL_JOB_TOOL_NAME,
  READ_JOB_OUTPUT_TOOL_NAME,
  RUN_BACKGROUND_TOOL_NAME,
  RUN_COMMAND_TOOL_NAME,
  TerminalPtyActionSchema,
  type TerminalFailureKind,
  type TerminalOutputItem,
} from '@terra/shared-dto-chat-types';
import type { PortalToolHandler, PortalToolHandlerResult } from '../types';
import { runInteractiveSessionAction } from './terminal-interactive-session';

const ShellSchema = z.enum(['bash', 'zsh', 'sh', 'powershell', 'cmd']);

const CommandExecutorInputSchema = z.object({
  toolName: z.string().optional(),
  command: z.string().optional(),
  displayCommand: z.string().optional(),
  executionCommand: z.string().optional(),
  description: z.string().optional(),
  cwd: z.string().optional(),
  isBackground: z.boolean().optional(),
  timeoutMs: z.number().int().positive().optional(),
  idleTimeoutMs: z.number().int().positive().optional(),
  killOnTimeout: z.boolean().optional(),
  shell: ShellSchema.optional(),
  chatId: z.string().optional(),
  jobId: z.string().optional(),
  tailBytes: z.number().int().positive().optional(),
  ptyId: z.string().optional(),
  ptyAction: TerminalPtyActionSchema.optional(),
  ptyInput: z.string().optional(),
  waitMs: z.number().int().positive().optional(),
});

type CommandExecutorContext = {
  ensureTerminalSession: (params: {
    sessionId: string;
    cwd?: string;
    source: 'ai';
    chatId?: string;
    toolCallId?: string;
    isBackground?: boolean;
  }) => Promise<{ terminalId: string; isNewSession: boolean }>;
};

function baseItem({
  batchId,
  toolCallId,
  command,
}: {
  batchId?: string;
  toolCallId: string;
  command: string;
}): Pick<TerminalOutputItem, 'batchId' | 'toolCallId' | 'command'> {
  return { batchId, toolCallId, command };
}

function failureKindForRun(result: RunCommandResult): TerminalFailureKind {
  if (result.status === 'failed-to-start') {
    return 'transport';
  }

  if (result.status === 'timed-out') {
    return 'timeout';
  }

  if (result.status === 'killed') {
    return 'abort';
  }

  return 'command';
}

function runResultToItem({
  result,
  batchId,
  toolCallId,
  command,
}: {
  result: RunCommandResult;
  batchId?: string;
  toolCallId: string;
  command: string;
}): PortalToolHandlerResult {
  const output =
    result.status === 'failed-to-start'
      ? (result.error ?? 'Command failed to start')
      : result.output;
  const succeeded =
    result.status === 'completed' ? true : result.status === 'backgrounded';

  return {
    status: succeeded ? 'success' : 'error',
    result: {
      ...baseItem({ batchId, toolCallId, command }),
      exitCode: result.exitCode ?? -1,
      output,
      outputLength: output.length,
      isTimeout: result.timedOut,
      failureKind: failureKindForRun(result),
      timedOut: result.timedOut,
      idleTimedOut: result.idleTimedOut,
      backgrounded: result.backgrounded,
      jobId: result.jobId,
      logPath: result.logPath,
      cwdAfter: result.cwdAfter,
      durationMs: result.durationMs,
      truncated: result.truncated,
      outputBytes: result.outputBytes,
      shell: result.shell,
      platform: result.platform,
    },
  };
}

function screenToItem({
  screen,
  batchId,
  toolCallId,
  command,
}: {
  screen: InteractivePtyScreen | null;
  batchId?: string;
  toolCallId: string;
  command: string;
}): PortalToolHandlerResult {
  if (!screen) {
    const output = 'Interactive session not found';

    return {
      status: 'error',
      result: {
        ...baseItem({ batchId, toolCallId, command }),
        exitCode: -1,
        output,
        outputLength: output.length,
        failureKind: 'tracker',
      },
    };
  }

  return {
    status: 'success',
    result: {
      ...baseItem({ batchId, toolCallId, command }),
      exitCode: screen.exitCode ?? 0,
      output: screen.screen,
      outputLength: screen.screen.length,
      failureKind: 'command',
      ptyId: screen.ptyId,
      ptyAlive: screen.alive,
      screen: screen.screen,
      shell: screen.shell,
    },
  };
}

function errorItem({
  batchId,
  toolCallId,
  command,
  message,
  failureKind = 'transport',
}: {
  batchId?: string;
  toolCallId: string;
  command: string;
  message: string;
  failureKind?: TerminalFailureKind;
}): PortalToolHandlerResult {
  return {
    status: 'error',
    result: {
      ...baseItem({ batchId, toolCallId, command }),
      exitCode: -1,
      output: message,
      outputLength: message.length,
      failureKind,
    },
  };
}

function showTerminalTab({
  sessionId,
  isNewSession,
}: {
  sessionId: string;
  isNewSession: boolean;
}): void {
  if (isNewSession) {
    useBottomPanelStore.getState().setTab('terminal');
  }

  if (useBottomPanelStore.getState().isOpen) {
    useTerminalStore.getState().setActiveTerminalTab(sessionId);
  }
}

export function createTerminalExecutionHandler({
  ensureTerminalSession,
}: CommandExecutorContext): PortalToolHandler {
  return async ({ input: rawInput, toolCallId, batchId, sessionId }) => {
    const parsed = CommandExecutorInputSchema.safeParse(rawInput);

    if (!parsed.success) {
      return errorItem({
        batchId,
        toolCallId,
        command: '',
        message: `Invalid command request: ${parsed.error.message}`,
      });
    }

    const input = parsed.data;
    const toolName = input.toolName ?? RUN_COMMAND_TOOL_NAME;
    const command = input.executionCommand ?? input.command ?? '';
    const displayCommand = input.displayCommand ?? command;
    const chatId = input.chatId ?? sessionId;
    const resolvedSessionId = sessionId || batchId;
    const api = window.electronAPI.command;

    if (!api) {
      return errorItem({
        batchId,
        toolCallId,
        command: displayCommand,
        message: 'Command execution is not available in this build',
      });
    }

    try {
      if (toolName === READ_JOB_OUTPUT_TOOL_NAME) {
        if (!input.jobId) {
          return errorItem({
            batchId,
            toolCallId,
            command: displayCommand,
            message: 'jobId is required',
            failureKind: 'tracker',
          });
        }

        const job = await api.readOutput({
          jobId: input.jobId,
          tailBytes: input.tailBytes,
        });

        if (!job) {
          return errorItem({
            batchId,
            toolCallId,
            command: displayCommand,
            message: `Background job not found: ${input.jobId}`,
            failureKind: 'tracker',
          });
        }

        return {
          status: 'success',
          result: {
            ...baseItem({ batchId, toolCallId, command: job.command }),
            exitCode: job.exitCode ?? -1,
            output: job.output,
            outputLength: job.output.length,
            failureKind: 'command',
            jobId: job.jobId,
            jobStatus: job.status,
            backgrounded: job.status === 'running',
            logPath: job.logPath,
            outputBytes: job.outputBytes,
            durationMs: (job.endedAt ?? Date.now()) - job.startedAt,
          },
        };
      }

      if (toolName === KILL_JOB_TOOL_NAME) {
        if (!input.jobId) {
          return errorItem({
            batchId,
            toolCallId,
            command: displayCommand,
            message: 'jobId is required',
            failureKind: 'tracker',
          });
        }

        const job = await api.kill(input.jobId);

        if (!job) {
          return errorItem({
            batchId,
            toolCallId,
            command: displayCommand,
            message: `Background job not found: ${input.jobId}`,
            failureKind: 'tracker',
          });
        }

        const output = `Job ${job.jobId} ${job.status}`;

        return {
          status: 'success',
          result: {
            ...baseItem({ batchId, toolCallId, command: job.command }),
            exitCode: job.exitCode ?? 0,
            output,
            outputLength: output.length,
            failureKind: 'command',
            jobId: job.jobId,
            jobStatus: job.status,
            logPath: job.logPath,
          },
        };
      }

      if (toolName === INTERACTIVE_SESSION_TOOL_NAME) {
        const screen = await runInteractiveSessionAction({
          input,
          chatId,
          sessionId: resolvedSessionId,
          toolCallId,
        });

        return screenToItem({
          screen,
          batchId,
          toolCallId,
          command: displayCommand,
        });
      }

      if (!command) {
        return errorItem({
          batchId,
          toolCallId,
          command: '',
          message: 'Command is required',
        });
      }

      const isBackground =
        toolName === RUN_BACKGROUND_TOOL_NAME || input.isBackground === true;
      const existingTabs = useTerminalStore.getState().terminalTabs;
      const isNewTab = !existingTabs.some(
        (tab) => tab.sessionId === resolvedSessionId,
      );
      const terminalSession = await ensureTerminalSession({
        sessionId: resolvedSessionId,
        cwd: input.cwd,
        source: 'ai',
        chatId,
        toolCallId,
        isBackground,
      });

      showTerminalTab({
        sessionId: resolvedSessionId,
        isNewSession: isNewTab || terminalSession.isNewSession,
      });
      useTerminalStore
        .getState()
        .linkToolCallToTab(toolCallId, resolvedSessionId);

      if (!isBackground) {
        useTerminalStore
          .getState()
          .markTerminalCommandRunning(resolvedSessionId);
      }

      const result = await api
        .run({
          command,
          displayCommand,
          description: input.description,
          toolCallId,
          chatId,
          sessionId: resolvedSessionId,
          cwd: input.cwd,
          shell: input.shell,
          timeoutMs: input.timeoutMs,
          idleTimeoutMs: input.idleTimeoutMs,
          killOnTimeout: input.killOnTimeout,
          background: isBackground,
        })
        .finally(() => {
          if (!isBackground) {
            useTerminalStore
              .getState()
              .markTerminalCommandFinished(resolvedSessionId);
          }
        });

      return runResultToItem({
        result,
        batchId,
        toolCallId,
        command: displayCommand,
      });
    } catch (error: unknown) {
      console.warn('[TerminalExecutionHandler] Command execution failed', {
        error: error instanceof Error ? error : new Error(String(error)),
        props: { toolCallId, batchId, toolName },
      });

      return errorItem({
        batchId,
        toolCallId,
        command: displayCommand,
        message:
          error instanceof Error ? error.message : 'Command execution failed',
      });
    }
  };
}
